import CustomTable from '@/components/CustomTable/CustomTable.vue';
import CustomTabs from '@/components/CustomTab/CustomTab.vue';
import Swal from 'sweetalert2';
import { useToast } from "@/components/Toast/useToast";
import AdminNavBar from '@/components/AdminNavBar/AdminNavBar.vue';
import { API_BASE_URL } from "@/config";
import { validateToken } from '@/utils/auth';
import { format_date } from '@/utils/date_formatter';
import { validate_first_name, validate_last_name, validate_email, validate_phone_number, validate_invalid_characters} from '@/utils/validations.js';

const ROLE_MAP = {
  Clients: 1,
  Admins: 2,
  Customers: 3,
  ClientAdmins: 4,
};

export default {
  name: 'SystemUsers',
  setup() {
    const { showSuccess, showError } = useToast();
    
    return {
      showSuccess,
      showError
    };
  },
  components: {
    AdminNavBar,
    CustomTable,
    CustomTabs
  },
  data: () => ({
    first_name: '',
    last_name: '',
    email: '',
    phone_number: '',
    role: '',
    currentTab: localStorage.getItem('usersTab') || 'Customers',
    isFirstNameValid: false,
    isLastNameValid: false,
    isEmailValid: false,
    isPhoneNumberValid: false,
    columns: [
      { label: 'Name', key: 'name_email' },
      { label: 'Phone', key: 'phone_number' },
      { label: 'Date Joined', key: 'date_joined' },
    ],
    tabs: [
      { label: 'Client Admins', value: 'ClientAdmins' },
      { label: 'Clients', value: 'Clients' },
      { label: 'Customers', value: 'Customers' },
      { label: 'System Admins', value: 'Admins'}
    ]
  }),
  computed: {
    isFormValid() {
      return this.isFirstNameValid && 
             this.isLastNameValid && 
             this.isEmailValid && 
             this.isPhoneNumberValid && 
             this.role !== '';
    }
  },
  watch: {
    currentTab(newTab) {
      localStorage.setItem('usersTab', newTab);
      localStorage.removeItem(`page_system_users_${newTab}`);
    }
  },
  methods: {
    formatDate(date) {
      return format_date(date);
    },
    fetchUsers(page = 1, search = '') {
      const TOKEN = localStorage.getItem('token');
      const role_id = ROLE_MAP[this.currentTab];
      return new Promise((resolve, reject) => {
        let url = `${API_BASE_URL}/verify_api/verify_admin_api/get_users_api/?page=${page}&role_id=${role_id}`;
        if (search) {
          url += `&search=${encodeURIComponent(search)}`;
        }
        $.ajax({
          url,
          method: 'GET',
          dataType: 'json',
          headers: { Authorization: `Token ${TOKEN}` },
          success: function (data) {
            resolve({
              count: data.count,
              results: data.results,
            });
          },
          error: function (xhr, status, error) {
            reject(error);
          }
        });
      });
    },
    validate_inputs() {
      if (!validate_first_name(this.first_name)) return false;
      if (!validate_last_name(this.last_name)) return false;
      if (!validate_email(this.email)) return false;
      if (!validate_phone_number(this.phone_number)) return false;
      
      return true;
    },
    validate_phone_number_input() {
      this.phone_number = this.phone_number.replace(/\D/g, '');
      if (this.phone_number.length > 10) {
        this.phone_number = this.phone_number.substring(0, 10);
      }
      this.isPhoneNumberValid = validate_phone_number(this.phone_number);
    },
    validate_email_input() {
      this.isEmailValid = validate_email(this.email);
    },
    validate_name(field_name) {
      this[field_name] = validate_invalid_characters(this[field_name]);
      if (field_name === 'first_name') {
        this.isFirstNameValid = validate_first_name(this.first_name);
      } else if (field_name === 'last_name') {
        this.isLastNameValid = validate_last_name(this.last_name);
      }
    },
    add_user() {
      if (!this.validate_inputs()) {
        return;
      }
      const TOKEN = localStorage.getItem('token');
      const BASE_URL = API_BASE_URL;
      if (!BASE_URL) {
        this.showError('.error-message', 'Server configuration error. Please try again later.');
        return;
      }
      const ADD_USER_URL = `${BASE_URL}/verify_api/system_management_api/add_user_api/`;
      const FORM_DATA = {
        first_name: this.first_name,
        last_name: this.last_name,
        email: this.email,
        phone_number: this.phone_number,
        role: this.role
      };
      $.ajax({
        method: "POST",
        url: ADD_USER_URL,
        data: JSON.stringify(FORM_DATA),
        contentType: 'application/json',
        headers: {
          Authorization: `Token ${TOKEN}`
        },
        beforeSend: () => {
        },
        success: (response) => {
          this.showSuccess(response.status);
          setTimeout(() => {
            window.location.reload();
          }, 1500);
        },
        error: (error) => {
          this.showError(error.responseJSON?.error || 'An error occurred while creating the user.');
        }
      });
    },
    view_admin(user_id) {
      this.$router.push({ name: 'view_admin', params: { user_id: user_id } });
    },
    view_client(user_id) {
      this.$router.push({ name: 'view_client', params: { user_id: user_id } });
    },
    view_customer(user_id) {
      this.$router.push({ name: 'view_customer', params: { user_id: user_id } });
    },
    view_client_admin(user_id) {
      this.$router.push({ name: 'view_client_admin', params: { user_id: user_id } });
    },
    toggleUserStatus(user) {
      const action = user.is_active ? 'deactivate' : 'activate';
      const actionTitle = action === 'deactivate' ? 'Deactivate User' : 'Activate User';
      const actionText = action === 'deactivate' 
        ? 'Are you sure you want to deactivate this user? They will be logged out and unable to log back in.'
        : 'Are you sure you want to activate this user? They will be able to log in again.';
      const actionButtonText = action === 'deactivate' ? 'Yes, deactivate' : 'Yes, activate';
      
      Swal.fire({
        title: actionTitle,
        text: actionText,
        icon: 'warning',
        showCancelButton: true,
        confirmButtonColor: action === 'deactivate' ? '#d33' : '#28a745',
        cancelButtonColor: '#3085d6',
        confirmButtonText: actionButtonText,
        cancelButtonText: 'Cancel'
      }).then((result) => {
        if (result.isConfirmed) {
          const TOKEN = localStorage.getItem('token');
          const BASE_URL = API_BASE_URL;
          if (!BASE_URL) {
            this.showError('Server configuration error. Please try again later.');
            return;
          }
          const ACTION_URL = `${BASE_URL}/verify_api/verify_admin_api/${action}_user_api/${user.id}`;

          $.ajax({
            method: "POST",
            url: ACTION_URL,
            contentType: 'application/json',
            headers: {
              Authorization: `Token ${TOKEN}`
            },
            beforeSend: () => {
              Swal.fire({
                text: `${action === 'deactivate' ? 'Deactivating' : 'Activating'} user...`,
                allowEscapeKey: false,
                allowOutsideClick: false,
                didOpen: () => {
                  Swal.showLoading();
                }
              });
            },
            success: (response) => {
              Swal.fire({
                icon: 'success',
                text: response.message,
                confirmButtonText: 'OK'
              }).then(() => {
                // Refresh the table
                window.location.reload();
              });
            },
            error: (error) => {
              Swal.fire({
                icon: 'error',
                text: error.responseJSON?.error || `Failed to ${action} user. Please try again.`,
                confirmButtonText: 'OK'
              });
            }
          });
        }
      });
    }
  },
  mounted() {
    if (!validateToken(this.$router)) {
      return;
  }
    this.fetchUsers();
  }
};
